import type {
  HTMLAttributes,
  TdHTMLAttributes,
  ThHTMLAttributes,
  TableHTMLAttributes,
  ReactNode,
} from 'react';
import './Table.css';

export interface TableRootProps extends TableHTMLAttributes<HTMLTableElement> {
  /** Wrap in a horizontal-scroll container (on by default). */
  scroll?: boolean;
}

function TableRoot({ scroll = true, className, children, ...rest }: TableRootProps): JSX.Element {
  const table = (
    <table className={['data-table', className].filter(Boolean).join(' ')} {...rest}>
      {children}
    </table>
  );
  return scroll ? <div className="dsr-table-wrap">{table}</div> : table;
}

function Head({ children, ...rest }: HTMLAttributes<HTMLTableSectionElement>): JSX.Element {
  return <thead {...rest}>{children}</thead>;
}

function Body({ children, ...rest }: HTMLAttributes<HTMLTableSectionElement>): JSX.Element {
  return <tbody {...rest}>{children}</tbody>;
}

export interface RowProps extends HTMLAttributes<HTMLTableRowElement> {
  /** Hover highlight + pointer cursor — for click-through rows. */
  interactive?: boolean;
}

function Row({ interactive, className, children, ...rest }: RowProps): JSX.Element {
  const cls = [interactive ? 'dsr-row-interactive' : '', className].filter(Boolean).join(' ');
  return (
    <tr className={cls || undefined} {...rest}>
      {children}
    </tr>
  );
}

function HeaderCell({ children, ...rest }: ThHTMLAttributes<HTMLTableCellElement>): JSX.Element {
  return <th {...rest}>{children}</th>;
}

export type CellTone = 'default' | 'strong' | 'muted' | 'mono';

const CELL_TONE: Record<CellTone, string> = {
  default: '',
  strong: 'dsr-cell-strong',
  muted: 'muted',
  mono: 'dsr-cell-mono',
};

export interface CellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  tone?: CellTone;
}

function Cell({ tone = 'default', className, children, ...rest }: CellProps): JSX.Element {
  const cls = [CELL_TONE[tone], className].filter(Boolean).join(' ');
  return (
    <td className={cls || undefined} {...rest}>
      {children}
    </td>
  );
}

function Empty({ colSpan, children }: { colSpan: number; children: ReactNode }): JSX.Element {
  return (
    <tr>
      <td colSpan={colSpan} className="dsr-table-empty">
        {children}
      </td>
    </tr>
  );
}

// Compound table over the existing `.data-table` styling. Replaces the
// near-identical .rpt-table / .as-table / .sv-table duplicates.
export const Table = Object.assign(TableRoot, {
  Head,
  Body,
  Row,
  HeaderCell,
  Cell,
  Empty,
});
